import { add, startOfDay } from 'date-fns';
import type { Language } from '../../../src/api/langConfig';
import { PACKAGE_TEMP_STALE_MS } from '../api/constants';
import { toDateFromServer } from '../api/dates';
import type { MealPackage, ModelComplexity, UserMealPackageTemp } from '../types';

export const DEFAULT_NUM_DAYS_SHOW_PACKAGE = 20;
export const DEFAULT_PACKAGE_ORDERING_DAYS = 10;

export type PackageCatalogRules = {
    numDaysShowPackage: number;
    packageOrderingDays: number;
};

export function packageCatalogRulesFromModel(
    model: Pick<ModelComplexity, 'numDaysShowPackage' | 'packageOrderingDays'> | null | undefined,
): PackageCatalogRules {
    return {
        numDaysShowPackage: model?.numDaysShowPackage ?? DEFAULT_NUM_DAYS_SHOW_PACKAGE,
        packageOrderingDays: typeof model?.packageOrderingDays === 'number' && Number.isFinite(model.packageOrderingDays)
            ? model.packageOrderingDays
            : DEFAULT_PACKAGE_ORDERING_DAYS,
    };
}

/** Matches Packages PriceCardsDb catalog filter (still shows “too far” rows with disabled buy). */
export function isMealPackageVisibleInCatalog(
    mealPackage: Pick<MealPackage, 'start'>,
    asOf: Date = new Date(),
    rules?: PackageCatalogRules,
): boolean {
    const numDaysShowPackage = rules?.numDaysShowPackage ?? DEFAULT_NUM_DAYS_SHOW_PACKAGE;
    const packageStart = toDateFromServer(mealPackage.start);
    const showCutoff = add(asOf, { days: -numDaysShowPackage });
    return packageStart > showCutoff;
}

/** Whether checkout / Viva / placeOrderPackage may run for this catalog row. */
export function isMealPackagePurchasable(
    mealPackage: Pick<MealPackage, 'start' | 'end'>,
    asOf: Date = new Date(),
    rules?: PackageCatalogRules,
): boolean {
    const r = rules ?? packageCatalogRulesFromModel(null);
    if (!isMealPackageVisibleInCatalog(mealPackage, asOf, r)) {
        return false;
    }
    const packageStart = toDateFromServer(mealPackage.start);
    const packageEnd = toDateFromServer(mealPackage.end);
    const today = startOfDay(asOf);
    if (packageEnd < today) {
        return false;
    }
    const daysUntilStart = Math.ceil((startOfDay(packageStart).getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
    if (daysUntilStart > r.packageOrderingDays) {
        return false;
    }
    const effectiveStart = packageStart > asOf ? packageStart : asOf;
    if (effectiveStart > packageEnd) {
        return false;
    }
    return true;
}

/** First day to register package meals on purchase (full catalog period, not payment date). */
export function packageMealCopyStartDate(mealPackage: Pick<MealPackage, 'start'>): Date {
    return toDateFromServer(mealPackage.start);
}

export function packageTempReferenceMs(temp: UserMealPackageTemp): number | null {
    if (typeof temp.orderedAt === 'number' && temp.orderedAt > 0) {
        return temp.orderedAt;
    }
    if (typeof temp.startedAt === 'number' && temp.startedAt > 0) {
        return temp.startedAt;
    }
    return null;
}

export function isPackageTempStale(temp: UserMealPackageTemp, maxAgeMs: number = PACKAGE_TEMP_STALE_MS, now = Date.now()): boolean {
    const refMs = packageTempReferenceMs(temp);
    if (refMs == null) {
        return true;
    }
    return now - refMs > maxAgeMs;
}

export function packageNotOrderableMessage(lang: Language): string {
    return lang === 'el'
        ? 'Αυτό το πακέτο δεν είναι πλέον διαθέσιμο για αγορά. Επίλεξε ένα νέο πακέτο από τη λίστα.'
        : 'This package is no longer available to purchase. Please choose a new package from the list.';
}

export function packageTempStaleMessage(lang: Language): string {
    return lang === 'el'
        ? 'Η εκκρεμής παραγγελία πακέτου έληξε. Επίλεξε ξανά πακέτο από τη λίστα.'
        : 'Your pending package order has expired. Please choose a package again from the list.';
}

export function packageRecipientBlockedMessage(lang: Language): string {
    return lang === 'el'
        ? 'Αυτό το παιδί έχει ήδη ενεργό πακέτο για αυτή την περίοδο.'
        : 'This child already has an active package for this period.';
}
